import { createElement, useLayoutEffect, useRef, type HTMLAttributes, type ReactNode } from 'react';

type As = 'section' | 'div' | 'article' | 'main';

export interface RevealStaggerProps extends Omit<HTMLAttributes<HTMLElement>, 'as'> {
  as?: As;
  children?: ReactNode;
  /** Reveal on first paint (double rAF). Useful for above-the-fold blocks. */
  animateOnMount?: boolean;
}

/** Milliseconds between each item's transition start (index * step). */
const STAGGER_STEP_MS = 75;
const STAGGER_MAX_INDEX = 24;

/**
 * Which nodes get their own intersection observer and stagger delay.
 * - Optional: any descendant with `data-reveal-item` (non-nested under another marked node). Add `data-reveal-delay-add` (ms) for extra transition-delay on top of the stagger.
 * - Otherwise: direct children, with top-level `ul`/`ol` replaced by their `li`s so each card observes separately.
 * - `data-reveal-expand` on a wrapper replaces that node with its children (e.g. pillar grid + CTA row next to headline).
 */
function collectRevealTargets(root: HTMLElement): HTMLElement[] {
  const marked = root.querySelectorAll<HTMLElement>('[data-reveal-item]');
  if (marked.length > 0) {
    const out: HTMLElement[] = [];
    outer: for (const el of marked) {
      if (!root.contains(el)) continue;
      let p = el.parentElement;
      while (p && p !== root) {
        if (p.hasAttribute('data-reveal-item')) continue outer;
        p = p.parentElement;
      }
      out.push(el);
    }
    if (out.length > 0) return out;
  }

  let nodes = Array.from(root.children) as HTMLElement[];
  if (nodes.length === 0) return [];

  const flattenLists = (list: HTMLElement[]) =>
    list.flatMap((n) =>
      n.tagName === 'UL' || n.tagName === 'OL' ? (Array.from(n.children) as HTMLElement[]) : [n],
    );

  /** Explicitly split a wrapper so each child is observed (e.g. pillar grid beside other siblings). */
  const expandMarked = (list: HTMLElement[]) =>
    list.flatMap((n) =>
      n.hasAttribute('data-reveal-expand')
        ? (Array.from(n.children) as HTMLElement[])
        : [n],
    );

  nodes = flattenLists(nodes);
  nodes = expandMarked(nodes);
  nodes = flattenLists(nodes);

  if (nodes.length === 1) {
    const wrap = nodes[0];
    const kids = Array.from(wrap.children) as HTMLElement[];
    if (kids.length === 1 && (kids[0].tagName === 'UL' || kids[0].tagName === 'OL')) {
      return Array.from(kids[0].children) as HTMLElement[];
    }
    if (wrap.classList.contains('grid') && kids.length >= 2) {
      return kids;
    }
  }

  return nodes;
}

export default function RevealStagger({
  as = 'div',
  animateOnMount = false,
  className = '',
  children,
  ...rest
}: RevealStaggerProps) {
  const ref = useRef<HTMLElement | null>(null);

  /** useLayoutEffect: apply hidden state before first paint so off-screen rows do not flash visible (e.g. blog grid). */
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const root = ref.current;
    if (!root) return;

    const targets = collectRevealTargets(root);
    if (targets.length === 0) return;

    const prepareTargets = () => {
      targets.forEach((el, i) => {
        el.classList.add('reveal-item');
        const extraRaw = el.getAttribute('data-reveal-delay-add');
        const extra =
          extraRaw !== null && extraRaw !== '' ? parseInt(extraRaw, 10) : 0;
        const base = Math.min(i, STAGGER_MAX_INDEX) * STAGGER_STEP_MS;
        const delay = base + (Number.isNaN(extra) ? 0 : extra);
        el.style.setProperty('--reveal-stagger-delay', `${delay}ms`);
      });
    };

    const revealAll = () => {
      for (const el of targets) el.classList.add('reveal-item-in');
    };

    prepareTargets();

    const cleanupItemStyles = () => {
      for (const el of targets) {
        el.classList.remove('reveal-item', 'reveal-item-in');
        el.style.removeProperty('--reveal-stagger-delay');
      }
    };

    if (reduced) {
      revealAll();
      return cleanupItemStyles;
    }

    if (animateOnMount) {
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => revealAll());
      });
      return () => {
        cancelAnimationFrame(id);
        cleanupItemStyles();
      };
    }

    if (typeof IntersectionObserver === 'undefined') {
      revealAll();
      return cleanupItemStyles;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).classList.add('reveal-item-in');
            io.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' },
    );

    for (const el of targets) io.observe(el);
    return () => {
      io.disconnect();
      cleanupItemStyles();
    };
  }, [animateOnMount]);

  return createElement(
    as,
    {
      ref,
      className: ['reveal-stagger', className].filter(Boolean).join(' '),
      ...rest,
    },
    children,
  );
}
